import type { ReactNode } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LogOut,
  ChevronDown,
  Sparkles,
  LayoutDashboard,
  Eye,
  MessageSquare,
  Layers,
  Radio,
  Workflow,
  Activity,
  Settings as SettingsIcon,
  HelpCircle,
  Menu,
  X,
  RefreshCw,
  EyeOff,
  CheckCircle,
  HelpCircle as HelpIcon
} from "lucide-react";
import { useState, useEffect } from "react";
import { signOut, type AuthedUser } from "../lib/auth";
import { useMe } from "../lib/me";
import { cn } from "../lib/cn";
import { api } from "../lib/api";
import type { DashboardStats } from "../lib/me";

interface AppShellProps {
  user: AuthedUser;
  children: ReactNode;
}

const navItems = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/review", label: "Review", icon: Eye, badgeKey: "awaitingReview" },
  { to: "/cases", label: "Cases", icon: MessageSquare },
  { to: "/categories", label: "Categories", icon: Layers },
  { to: "/connectors", label: "Connectors", icon: Radio },
  { to: "/services", label: "Services", icon: Workflow },
  { to: "/audit", label: "Audit", icon: Activity },
  { to: "/settings", label: "Settings", icon: SettingsIcon },
  { to: "/help", label: "Help", icon: HelpCircle },
];

export function AppShell({ user, children }: AppShellProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { me, refresh } = useMe();
  const [menuOpen, setMenuOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [savingToggle, setSavingToggle] = useState<"autoReplies" | "dryRun" | null>(null);
  const [stats, setStats] = useState<DashboardStats | null>(null);

  const autoReplies = me.tenant?.settings?.autoRepliesEnabled ?? false;
  const dryRun = me.tenant?.settings?.dryRunMode ?? true;

  useEffect(() => {
    api.get<DashboardStats>("/api/stats")
      .then(setStats)
      .catch(() => { });
  }, [location.pathname]);

  const toggleSetting = async (key: "autoReplies" | "dryRun", next: boolean) => {
    setSavingToggle(key);
    try {
      await api.patch("/api/settings", key === "autoReplies" ? { autoRepliesEnabled: next } : { dryRunMode: next });
      await refresh();
    } finally {
      setSavingToggle(null);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/signin", { replace: true });
  };

  // Close mobile sidebar on navigate
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const initials = user.email
    .split("@")[0]
    ?.split(/[._-]/)
    .map((p) => p[0]?.toUpperCase())
    .filter(Boolean)
    .slice(0, 2)
    .join("") ?? "U";

  const displayName = user.email
    .split("@")[0]
    ?.split(/[._-]/)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ") ?? "User";

  const reviewBadgeCount = stats?.open.awaitingReview ?? 0;

  // Derive page titles
  const getPageTitle = () => {
    const current = navItems.find(item => {
      if (item.to === "/") return location.pathname === "/";
      return location.pathname.startsWith(item.to);
    });
    return current ? current.label : "Portal";
  };

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-slate-50/60 text-slate-800">

      {/* 1. Desktop Sidebar */}
      <aside className="hidden lg:flex flex-col w-66 shrink-0 border-r border-slate-200/80 bg-white">
        {/* Brand */}
        <div className="flex h-16 items-center gap-3 px-6 border-b border-slate-100">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-linear-to-tr from-brand-600 to-brand-500 text-white shadow-md shadow-brand-500/20">
            <Sparkles className="h-5 w-5" />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-bold tracking-tight text-slate-900">Triage</span>
            {/* <span className="text-[10px] font-semibold text-brand-600 truncate max-w-40">
              {me.tenant?.displayName ?? "Tenant Setup"}
            </span> */}
          </div>
        </div>

        {/* Sidebar Nav */}
        <nav className="flex-1 overflow-y-auto px-4 py-6 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const hasBadge = item.badgeKey === "awaitingReview" && reviewBadgeCount > 0;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 px-3.5 py-2.5 text-sm font-semibold rounded-xl transition-all duration-200",
                    isActive
                      ? "bg-slate-950 text-white shadow-sm"
                      : "text-slate-500 hover:bg-slate-100 hover:text-slate-950"
                  )
                }
              >
                <Icon className="h-4.5 w-4.5 shrink-0" />
                <span>{item.label}</span>
                {/* {hasBadge && (
                  <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 px-1.5 text-[10px] font-bold text-white ring-2 ring-white">
                    {reviewBadgeCount}
                  </span>
                )} */}
              </NavLink>
            );
          })}
        </nav>

        {/* Status Panel (Auto-Replies & Dry Run toggles) */}
        <div className="border-t border-slate-100 bg-slate-50/50 p-4 space-y-3">
          <div className="rounded-xl border border-slate-200/60 bg-white p-3 space-y-2.5 shadow-2xs">
            <Toggle
              label="Auto-replies"
              checked={autoReplies}
              onChange={(v) => toggleSetting("autoReplies", v)}
              busy={savingToggle === "autoReplies"}
            />
            <Toggle
              label="Dry-run"
              checked={dryRun}
              onChange={(v) => toggleSetting("dryRun", v)}
              busy={savingToggle === "dryRun"}
            />
          </div>
        </div>
      </aside>

      {/* 2. Mobile Sidebar Overlay & Drawer */}
      {sidebarOpen && (
        <div className="lg:hidden">
          <div className="fixed inset-0 z-40 bg-slate-950/40 backdrop-blur-xs" onClick={() => setSidebarOpen(false)} />
          <aside className="fixed bottom-0 top-0 left-0 z-50 flex flex-col w-72 bg-white border-r border-slate-200 animate-fade-in">
            {/* Header inside drawer */}
            <div className="flex h-16 items-center justify-between px-6 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-linear-to-tr from-brand-600 to-brand-500 text-white">
                  <Sparkles className="h-5 w-5" />
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-bold tracking-tight text-slate-900">Triage</span>
                  <span className="text-[10px] font-semibold text-brand-600">
                    {me.tenant?.displayName ?? "Setup"}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-slate-100 text-slate-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Mobile Nav items */}
            <nav className="flex-1 overflow-y-auto px-4 py-6 space-y-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const hasBadge = item.badgeKey === "awaitingReview" && reviewBadgeCount > 0;
                const isActive = location.pathname === item.to || (item.to !== "/" && location.pathname.startsWith(item.to));

                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={
                      cn(
                        "flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold transition-all duration-200",
                        isActive
                          ? "bg-slate-950 text-white"
                          : "text-slate-500 hover:bg-slate-100 hover:text-slate-950"
                      )
                    }
                  >
                    <Icon className="h-5 w-5 shrink-0" />
                    <span>{item.label}</span>
                    {hasBadge && (
                      <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 px-1.5 text-[10px] font-bold text-white">
                        {reviewBadgeCount}
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </nav>

            {/* Status Panel Mobile */}
            <div className="border-t border-slate-100 bg-slate-50/50 p-4 space-y-3">
              <div className="rounded-xl border border-slate-200/60 bg-white p-3 space-y-2.5 shadow-2xs">
                <Toggle
                  label="Auto-replies"
                  checked={autoReplies}
                  onChange={(v) => toggleSetting("autoReplies", v)}
                  busy={savingToggle === "autoReplies"}
                />
                <Toggle
                  label="Dry-run"
                  checked={dryRun}
                  onChange={(v) => toggleSetting("dryRun", v)}
                  busy={savingToggle === "dryRun"}
                />
              </div>
            </div>
          </aside>
        </div>
      )}

      {/* 3. Main Workspace Container */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">

        {/* Top Header/Navbar */}
        <header className="flex h-16 shrink-0 items-center justify-between border-b border-slate-200/80 bg-white px-4 sm:px-6 z-30">
          <div className="flex items-center gap-3">
            {/* Sidebar Toggle for Mobile */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="flex lg:hidden items-center justify-center h-10 w-10 rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-600 transition-colors"
            >
              <Menu className="h-5 w-5" />
            </button>

            {/* Dynamic Page Title */}
            <div>
              <h1 className="text-base font-bold text-slate-900 leading-none">{getPageTitle()}</h1>
              <div className="hidden sm:flex items-center gap-1.5 mt-1 text-[11px] text-slate-400 font-medium">
                <span>Triage Console</span>
                <span>/</span>
                <span className="text-slate-600 lowercase">{getPageTitle()}</span>
              </div>
            </div>
          </div>

          {/* Topbar Right Operations */}
          <div className="flex items-center gap-4">

            {/* Mode indicators */}
            <div className="hidden md:flex items-center gap-2">
              {dryRun && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-700 border border-amber-200/50">
                  <EyeOff className="h-3 w-3" /> Dry-run active
                </span>
              )}
            </div>

            {/* Profile Dropdown */}
            <div className="relative">
              <button
                onClick={() => setMenuOpen((v) => !v)}
                className="flex items-center gap-2 rounded-xl border border-slate-200/50 bg-slate-50/50 p-1 pr-2.5 hover:bg-slate-100/80 transition-colors"
              >
                <div className="flex h-7.5 w-7.5 items-center justify-center rounded-lg bg-linear-to-br from-brand-600 to-brand-700 text-xs font-semibold text-white">
                  {initials}
                </div>
                <div className="hidden sm:flex flex-col items-start text-left leading-none gap-0.5">
                  <span className="text-xs font-bold text-slate-700">{displayName}</span>
                  {me.tenant?.displayName && (
                    <span className="text-[10px] text-slate-400 font-medium">
                      {me.tenant.displayName}
                    </span>
                  )}
                </div>
                <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
              </button>

              {menuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
                  <div className="absolute right-0 mt-2 min-w-56 w-max max-w-xs rounded-2xl border border-slate-200 bg-white p-2 shadow-xl z-50 animate-scale-in">
                    <div className="border-b border-slate-100 px-3.5 py-3">
                      <div className="text-sm font-bold text-slate-950">{displayName}</div>
                      <div className="text-xs text-slate-500 mt-1 break-all">{user.email}</div>
                      {me.tenant?.displayName && (
                        <div className="mt-2 flex items-center gap-1.5">
                          <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Tenant:</span>
                          <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-semibold text-slate-700">
                            {me.tenant.displayName}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="mt-1">
                      <button
                        onClick={handleSignOut}
                        className="flex w-full items-center gap-2.5 rounded-xl px-3.5 py-2.5 text-left text-sm font-semibold text-rose-600 hover:bg-rose-50 transition-colors"
                      >
                        <LogOut className="h-4 w-4 text-rose-500" />
                        Sign out
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Dynamic Alert Banner */}
        <BlockedBanner tenant={me.tenant} />

        {/* Scrollable Workspace Content */}
        <main className="flex-1 overflow-y-auto bg-slate-50/50 px-4 sm:px-6 py-8 flex flex-col justify-between">
          <div className="w-full animate-fade-in">
            {children}
          </div>
          <footer className="mt-4 pt-3 border-t border-slate-200/40 text-left text-[10px] text-slate-400/80 font-semibold select-none">
            <span>&copy; {new Date().getFullYear()} <a href="https://appgambit.com" target="_blank" rel="noopener noreferrer" className="hover:text-slate-600 transition-colors underline">Appgambit</a>. All rights reserved.</span>
          </footer>
        </main>
      </div>

    </div>
  );
}

function BlockedBanner({ tenant }: { tenant: ReturnType<typeof useMe>["me"]["tenant"] }) {
  if (!tenant) return null;
  const quotaExhausted = tenant.quotaEmailsUsed >= tenant.quotaEmailsLimit;

  if (tenant.status === "suspended") {
    return (
      <Banner tone="red"
        title="Account suspended."
        body={<>AI actions are paused — inbound emails are not processed and replies won't be sent. Contact <a href="mailto:hello@appgambit.com" className="font-medium underline">hello@appgambit.com</a> to reactivate.</>}
      />
    );
  }
  if (tenant.status === "trial_expired") {
    return (
      <Banner tone="amber"
        title="Trial expired."
        body={<>Inbound emails are not being processed. Contact <a href="mailto:hello@appgambit.com" className="font-medium underline">hello@appgambit.com</a> to upgrade your plan.</>}
      />
    );
  }
  if (quotaExhausted) {
    return (
      <Banner tone="amber"
        title="Email quota reached."
        body={<>You've hit your monthly limit of {tenant.quotaEmailsLimit.toLocaleString()} emails. New inbound emails are not being processed until your quota resets or is increased. Contact <a href="mailto:hello@appgambit.com" className="font-medium underline">hello@appgambit.com</a> to raise your limit.</>}
      />
    );
  }
  return null;
}

function Banner({ tone, title, body }: { tone: "red" | "amber"; title: string; body: React.ReactNode }) {
  const cls = tone === "red"
    ? { wrap: "border-rose-200 bg-rose-50/80 text-rose-800", dot: "bg-rose-500 animate-pulse", strong: "text-rose-900" }
    : { wrap: "border-amber-200 bg-amber-50/80 text-amber-800", dot: "bg-amber-500", strong: "text-amber-900" };
  return (
    <div className={`border-b ${cls.wrap} backdrop-blur-xs`}>
      <div className="flex w-full items-start gap-3 px-6 py-3">
        <span className={`mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full ${cls.dot}`} />
        <div className="flex-1 text-sm leading-normal">
          <span className={`font-semibold ${cls.strong}`}>{title}</span>{" "}
          <span>{body}</span>
        </div>
      </div>
    </div>
  );
}

function Toggle({
  label,
  checked,
  onChange,
  busy,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  busy?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      disabled={busy}
      className="flex items-center justify-between w-full font-semibold text-slate-600 hover:text-slate-900 disabled:opacity-60 transition-colors cursor-pointer text-xs"
    >
      <span>{label}</span>
      <div className="flex items-center gap-2">
        <span
          className={cn(
            "relative inline-flex h-4.5 w-8.5 items-center rounded-full transition-colors duration-200",
            checked ? "bg-slate-950" : "bg-slate-200",
          )}
        >
          <span
            className={cn(
              "inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-xs transition-transform duration-200",
              checked ? "translate-x-4" : "translate-x-0.5",
            )}
          />
        </span>
        {busy && <RefreshCw className="h-3 w-3 animate-spin text-slate-400" />}
      </div>
    </button>
  );
}
